import {
	DISCOVERY_QUERY_TYPE_LABELS,
	type DiscoveryQueryType,
	PROMPT_GENERATOR_FRAMEWORK_LABELS,
	type PersonaDraft,
	type PromptGenerationResultItem,
	personaAskerType,
	personaCompanyLine,
	personaDisplayLabels,
	promptGeneratorFrameworkFor,
} from "@repo/core/client";

/**
 * The free tool's one export: a header row, one row per prompt, then one row
 * per persona. A Type column tells them apart, and each kind fills only the
 * columns that apply. Filter on Type to split them in a spreadsheet.
 *
 * Persona rows: Organization is filled for someone at an organization,
 * About them for a person; Location holds the persona's region; Tools holds
 * what they already have (an organization's tools, a person's coverage or
 * products).
 */
export const CSV_COLUMNS = [
	"Type",
	"Prompt or persona",
	"Framework",
	"Query type",
	"Segment",
	"Organization",
	"About them",
	"Tools",
	"Priorities",
	"Constraints",
	"What we'd tell AI",
	"Category",
	"Use case",
	"Location",
	"Budget",
] as const;

/** The columns the "See the file layout" preview shows. */
export const CSV_PREVIEW_COLUMNS = [
	"Type",
	"Prompt or persona",
	"Framework",
	"Segment",
	"Organization",
	"What we'd tell AI",
] as const;

export const CSV_MORE_COLUMNS =
	"Query type, About them, Tools, Priorities, Constraints, Category, Use case, Location and Budget";

export type CsvRow = Record<(typeof CSV_COLUMNS)[number], string>;

const LIST_SEPARATOR = "; ";

function emptyRow(): CsvRow {
	return Object.fromEntries(CSV_COLUMNS.map((c) => [c, ""])) as CsvRow;
}

export function queryTypeLabel(queryType: string): string {
	return (
		DISCOVERY_QUERY_TYPE_LABELS[queryType as DiscoveryQueryType] ??
		queryType
	);
}

export function promptCsvRows(
	prompts: readonly PromptGenerationResultItem[],
): CsvRow[] {
	return prompts.map((prompt) => ({
		...emptyRow(),
		Type: "Prompt",
		"Prompt or persona": prompt.query,
		Framework:
			PROMPT_GENERATOR_FRAMEWORK_LABELS[
				promptGeneratorFrameworkFor(prompt)
			],
		"Query type": queryTypeLabel(prompt.queryType),
		Segment: prompt.segment ?? "",
		Category: prompt.category ?? "",
		"Use case": prompt.useCase ?? "",
		Location: prompt.location ?? "",
		Budget: prompt.budget ?? "",
	}));
}

export function personaCsvRows(personas: readonly PersonaDraft[]): CsvRow[] {
	const labels = personaDisplayLabels(personas);
	return personas.map((persona, index) => {
		const individual = personaAskerType(persona) === "individual";
		return {
			...emptyRow(),
			Type: "Persona",
			"Prompt or persona": labels[index] ?? "",
			Segment: persona.segment ?? "",
			Organization: individual ? "" : personaCompanyLine(persona),
			"About them": individual ? (persona.household ?? "") : "",
			Tools: persona.techStack.join(LIST_SEPARATOR),
			Priorities: persona.priorities.join(LIST_SEPARATOR),
			Constraints: persona.constraints.join(LIST_SEPARATOR),
			"What we'd tell AI": persona.contextText,
			Location: persona.region ?? "",
		};
	});
}

/**
 * Quote every cell, and defuse spreadsheet formulas: a cell that starts with
 * = + - @ (or a tab / carriage return) gets a leading apostrophe so Excel and
 * Sheets show it as text instead of evaluating model-written content.
 */
export function escapeCsvCell(value: string): string {
	const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
	return `"${safe.replace(/"/g, '""')}"`;
}

/** The whole file. Starts with a BOM so Excel reads it as UTF-8. */
export function buildResultsCsv(
	prompts: readonly PromptGenerationResultItem[],
	personas: readonly PersonaDraft[],
): string {
	const rows = [...promptCsvRows(prompts), ...personaCsvRows(personas)];
	const lines = [
		CSV_COLUMNS.map(escapeCsvCell).join(","),
		...rows.map((row) =>
			CSV_COLUMNS.map((column) => escapeCsvCell(row[column])).join(","),
		),
	];
	return `﻿${lines.join("\n")}`;
}

/** "Copy All": the prompts one per line, then each persona and their line. */
export function buildResultsClipboardText(
	prompts: readonly PromptGenerationResultItem[],
	personas: readonly PersonaDraft[],
): string {
	const text = prompts.map((prompt) => prompt.query).join("\n");
	if (personas.length === 0) {
		return text;
	}
	const labels = personaDisplayLabels(personas);
	const personaLines = personas.map(
		(persona, index) => `${labels[index] ?? ""}: ${persona.contextText}`,
	);
	return `${text}\n\nPersonas\n${personaLines.join("\n")}`;
}
